import {
  NotificationsOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  ShieldOutlined,
} from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState, useEffect } from 'react';

import { Navbar as AdminNavbar } from '@/components/admin/general/Navbar';
import { MfaSetupModal } from '@/components/auth/MfaSetupModal';
import { ErrorState, LoadingState } from '@/components/common';
import { Navbar as SuperAdminNavbar } from '@/components/super-admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useUserDetails,
  useUpdateNotificationPreferences,
  useUpdateUser,
} from '@/hooks/queries/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { mfaService } from '@/services/mfa.service';
import type { MfaFactor } from '@/services/mfa.service';
import { showSuccessToast, showErrorToast } from '@/utils/toast';

export const ConfigurationPage: React.FC = () => {
  const { user, logout } = useAuth();

  // Fetch user details to get current notification preferences
  const { data: userDetails, isLoading, error, refetch } = useUserDetails(user?.id);

  // Update notification preferences mutation
  const updatePreferencesMutation = useUpdateNotificationPreferences();
  const updateUserMutation = useUpdateUser();

  // Local state for notification preferences
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // MFA state
  const [isMfaSetupModalOpen, setIsMfaSetupModalOpen] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<MfaFactor[]>([]);
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [isLoadingMfa, setIsLoadingMfa] = useState(false);

  // Initialize state from fetched user details
  useEffect(() => {
    if (userDetails) {
      setNotificationsEnabled(userDetails.notificationsEnabled);
      setEmailNotifications(userDetails.emailNotificationsEnabled);
      setPushNotifications(userDetails.pushNotificationsEnabled);
    }
  }, [userDetails]);

  // Load MFA factors on mount
  useEffect(() => {
    void loadMfaFactors();
  }, []);

  const loadMfaFactors = async () => {
    setIsLoadingMfa(true);
    try {
      const factors = await mfaService.listFactors();
      setMfaFactors(factors);
      setIsMfaEnabled(factors.some(f => f.status === 'verified'));
    } catch (_error) {
      // Silent fail - it's normal to not have MFA set up yet
      setMfaFactors([]);
      setIsMfaEnabled(false);
    } finally {
      setIsLoadingMfa(false);
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const NavbarComponent = isSuperAdmin ? SuperAdminNavbar : AdminNavbar;
  const navItems = isSuperAdmin ? superAdminNavItems : adminNavItems;

  const handleSaveChanges = async () => {
    if (!user?.id) return;

    try {
      await updatePreferencesMutation.mutateAsync({
        userId: user.id,
        preferences: {
          notificationsEnabled,
          emailNotificationsEnabled: emailNotifications,
          pushNotificationsEnabled: pushNotifications,
        },
      });
      showSuccessToast('Configuración guardada correctamente');
    } catch (_error) {
      showErrorToast('Error al guardar la configuración');
    }
  };

  const handleCancel = () => {
    // Reset to original values from backend
    if (userDetails) {
      setNotificationsEnabled(userDetails.notificationsEnabled);
      setEmailNotifications(userDetails.emailNotificationsEnabled);
      setPushNotifications(userDetails.pushNotificationsEnabled);
    }
  };

  // Master switch logic - disable all when master is disabled
  const handleNotificationsEnabledChange = (checked: boolean) => {
    setNotificationsEnabled(checked);
    if (!checked) {
      setEmailNotifications(false);
      setPushNotifications(false);
    }
  };

  const validatePasswordForm = (): boolean => {
    const errors: {
      currentPassword?: string;
      newPassword?: string;
      confirmPassword?: string;
    } = {};

    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'La contraseña actual es requerida';
    }

    if (!passwordForm.newPassword) {
      errors.newPassword = 'La nueva contraseña es requerida';
    } else if (passwordForm.newPassword.length < 6) {
      errors.newPassword = 'La contraseña debe tener al menos 6 caracteres';
    } else if (passwordForm.newPassword.length > 100) {
      errors.newPassword = 'La contraseña no debe exceder 100 caracteres';
    }

    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Confirma la nueva contraseña';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = async () => {
    if (!user?.id) return;
    if (!validatePasswordForm()) return;

    try {
      await updateUserMutation.mutateAsync({
        userId: user.id,
        data: {
          password: passwordForm.newPassword,
        },
      });
      showSuccessToast('Contraseña actualizada correctamente');
      setIsPasswordModalOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (_error) {
      showErrorToast('Error al actualizar la contraseña. Verifica tu contraseña actual.');
    }
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // MFA Handlers
  const handleMfaSetupSuccess = async () => {
    await loadMfaFactors();
    showSuccessToast('MFA configurado exitosamente. Por favor, inicia sesión nuevamente.');
    
    // Logout user after MFA setup
    setTimeout(() => {
      void logout();
    }, 2000); // Give user time to see the success message
  };

  const handleDisableMfa = async () => {
    const verifiedFactor = mfaFactors.find(f => f.status === 'verified');
    if (!verifiedFactor) return;

    if (!confirm('¿Estás seguro de que deseas desactivar la autenticación de dos factores?')) {
      return;
    }

    try {
      await mfaService.unenrollMFA(verifiedFactor.id);
      await loadMfaFactors();
      showSuccessToast('Autenticación de dos factores desactivada');
    } catch (_error) {
      showErrorToast('Error al desactivar la autenticación de dos factores');
    }
  };

  return (
    <div className="min-h-screen">
      <NavbarComponent navItems={navItems} userName={user?.name || 'Usuario'} />

      <div className="px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-brand-white">Configuración</h1>
          <p className="text-muted mt-1">Administra las preferencias de tu cuenta</p>
        </div>

        {/* Loading State */}
        {isLoading && <LoadingState message="Cargando configuración..." />}

        {/* Error State */}
        {error && (
          <ErrorState
            title="Error al cargar la configuración"
            message="No se pudo cargar la configuración de tu cuenta"
            error={error}
            showRetry
            onRetry={() => void refetch()}
          />
        )}

        {/* Configuration Content */}
        {!isLoading && !error && userDetails && (
          <div className="space-y-6">
            {/* Notifications Settings */}
            <CardContainer>
              <div className="">
                <div className="flex items-center gap-3 mb-4">
                  <NotificationsOutlined className="text-muted" />
                  <div>
                    <h2 className="text-lg font-semibold text-brand-white">Notificaciones</h2>
                    <p className="text-sm text-muted">
                      Configura cómo y cuándo quieres recibir notificaciones
                    </p>
                  </div>
                </div>

                <div className="space-y-4 mt-6">
                  {/* Master Switch - All Notifications */}
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <div>
                      <p className="text-sm font-medium text-brand-white">Activar Notificaciones</p>
                      <p className="text-sm text-muted">
                        Interruptor principal para activar/desactivar todas las notificaciones
                      </p>
                    </div>
                    <label
                      className="relative inline-flex items-center cursor-pointer"
                      aria-label="Activar Notificaciones"
                    >
                      <input
                        id="notificationsEnabled"
                        type="checkbox"
                        checked={notificationsEnabled}
                        onChange={e => handleNotificationsEnabledChange(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-white/30 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-blue"></div>
                    </label>
                  </div>
                </div>
              </div>
            </CardContainer>

            {/* Security Settings */}
            <CardContainer>
              <div className="">
                <div className="flex items-center gap-3 mb-4">
                  <LockOutlined className="text-muted" />
                  <div>
                    <h2 className="text-lg font-semibold text-brand-white">Seguridad</h2>
                    <p className="text-sm text-muted">Opciones de seguridad y autenticación</p>
                  </div>
                </div>

                <div className="space-y-6 mt-6">
                  {/* Change Password */}
                  <div className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-brand-white">Cambiar Contraseña</p>
                        <p className="text-sm text-muted mt-1">
                          Actualiza tu contraseña de acceso al sistema
                        </p>
                      </div>
                      <Button
                        variant="white"
                        size="small"
                        onClick={() => setIsPasswordModalOpen(true)}
                      >
                        Actualizar Contraseña
                      </Button>
                    </div>
                  </div>

                  {/* Two-Factor Authentication */}
                  <div className="py-3 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <ShieldOutlined fontSize="small" className="text-muted" />
                          <p className="text-sm font-medium text-brand-white">
                            Autenticación de Dos Factores (2FA)
                          </p>
                        </div>
                        <p className="text-sm text-muted mt-1">
                          {isMfaEnabled
                            ? 'Tu cuenta está protegida con autenticación de dos factores'
                            : 'Agrega una capa adicional de seguridad a tu cuenta'}
                        </p>
                        {isMfaEnabled && mfaFactors.length > 0 && (
                          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
                            <span className="text-xs font-medium text-green-400">✓ Activado</span>
                          </div>
                        )}
                      </div>
                      <div>
                        {isLoadingMfa ? (
                          <Button variant="white" size="small" disabled>
                            Cargando...
                          </Button>
                        ) : isMfaEnabled ? (
                          <Button
                            variant="white"
                            size="small"
                            onClick={() => void handleDisableMfa()}
                          >
                            Desactivar 2FA
                          </Button>
                        ) : (
                          <Button
                            variant="black"
                            size="small"
                            onClick={() => setIsMfaSetupModalOpen(true)}
                          >
                            Configurar 2FA
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContainer>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="white"
                onClick={handleCancel}
                disabled={updatePreferencesMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="black"
                onClick={() => void handleSaveChanges()}
                disabled={updatePreferencesMutation.isPending}
              >
                {updatePreferencesMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <button
              type="button"
              className="fixed inset-0 transition-opacity bg-black opacity-60"
              onClick={handleClosePasswordModal}
              aria-label="Cerrar modal"
            ></button>

            {/* Modal */}
            <div className="z-50 inline-block align-bottom bg-surface rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start mb-4">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-500/10 sm:mx-0 sm:h-10 sm:w-10">
                    <LockOutlined className="text-brand-blue" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-brand-white">
                      Cambiar Contraseña
                    </h3>
                    <p className="text-sm text-muted mt-1">
                      Actualiza tu contraseña de acceso al sistema
                    </p>
                  </div>
                </div>

                {/* Password Form */}
                <div className="space-y-4 mt-4">
                  {/* Current Password */}
                  <div>
                    <label
                      htmlFor="currentPassword"
                      className="block text-sm font-medium text-brand-white mb-1"
                    >
                      Contraseña Actual
                    </label>
                    <div className="relative">
                      <input
                        id="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={e =>
                          setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))
                        }
                        className={`w-full px-3 py-2 pr-10 border bg-surface text-brand-white placeholder:text-muted ${
                          passwordErrors.currentPassword ? 'border-red-500' : 'border-border'
                        } rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue`}
                        placeholder="Ingresa tu contraseña actual"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff className="h-5 w-5 text-muted" />
                        ) : (
                          <Visibility className="h-5 w-5 text-muted" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <p className="text-sm text-red-400 mt-1">{passwordErrors.currentPassword}</p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-sm font-medium text-brand-white mb-1"
                    >
                      Nueva Contraseña
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={e =>
                          setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))
                        }
                        className={`w-full px-3 py-2 pr-10 border bg-surface text-brand-white placeholder:text-muted ${
                          passwordErrors.newPassword ? 'border-red-500' : 'border-border'
                        } rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue`}
                        placeholder="Mínimo 6 caracteres"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <VisibilityOff className="h-5 w-5 text-muted" />
                        ) : (
                          <Visibility className="h-5 w-5 text-muted" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="text-sm text-red-400 mt-1">{passwordErrors.newPassword}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-brand-white mb-1"
                    >
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={e =>
                          setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))
                        }
                        className={`w-full px-3 py-2 pr-10 border bg-surface text-brand-white placeholder:text-muted ${
                          passwordErrors.confirmPassword ? 'border-red-500' : 'border-border'
                        } rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue`}
                        placeholder="Confirma tu nueva contraseña"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff className="h-5 w-5 text-muted" />
                        ) : (
                          <Visibility className="h-5 w-5 text-muted" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <p className="text-sm text-red-400 mt-1">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-surface border-t border-border px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <Button
                  variant="black"
                  onClick={() => void handlePasswordChange()}
                  disabled={updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? 'Actualizando...' : 'Actualizar Contraseña'}
                </Button>
                <Button variant="white" onClick={handleClosePasswordModal}>
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MFA Setup Modal */}
      <MfaSetupModal
        isOpen={isMfaSetupModalOpen}
        onClose={() => setIsMfaSetupModalOpen(false)}
        onSuccess={() => void handleMfaSetupSuccess()}
      />
    </div>
  );
};
